import React from 'react';
import {useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";
import {useGetTaxCodesQuery, useGetWorkspaceQuery, usePutWorkspaceMutation} from "../../store/Api";
import {ContextualMenu, Icon, IContextualMenuItem} from "@fluentui/react";
import {Dropdown} from "@fluentui/react/lib/Dropdown";
import {ContextualMenuItemType} from "@fluentui/react";
import {useApplyToWorkspaceItems} from "./useApplyToWorkspaceItems";

const WorkspaceTaxCodeHeader: React.FC = () => {
    const selectedWorkspace = useSelector((state: RootState) => state.settings.selectedWorkspaceId);
    if (selectedWorkspace == null) return <>Tax Code</>
    return <WorkspaceTaxCodeEntry workspace={selectedWorkspace} />
}

interface IWorkspaceTaxCodeEntryProps {
    workspace: string;
    isExample?: boolean;
}

const WorkspaceTaxCodeEntry: React.FC<IWorkspaceTaxCodeEntryProps> = (props) => {
    const workspace = useGetWorkspaceQuery(props.workspace, {skip: !props.workspace || props.workspace.trim().length === 0});
    const [showMenu, setShowMenu] = React.useState<boolean>(false);
    const headerRef = React.useRef<HTMLSpanElement>(null);
    const [val, setVal] = React.useState<string | undefined>(undefined);
    const [patchWorkspace] = usePutWorkspaceMutation();
    const taxCodes = useGetTaxCodesQuery();
    const {apply, applying, items} = useApplyToWorkspaceItems(props.workspace);
    React.useEffect(() => {
        if (workspace.data) {
            setVal(workspace.data.defaultTaxCode ?? "");
        }
    }, [workspace.data?.defaultTaxCode]);

    // Every line, not just the default: writes taxCode + taxRate exactly as the cell's own dropdown does.
    const applyToAll = (code: string) => {
        if (props.isExample) return;
        setShowMenu(false);
        void apply(`tax code "${code}"`, () => true, () => ({taxCode: code, taxRate: getTaxRateFromCode(code)}));
    };

    const update = (v: string | undefined) => {
        if(props.isExample) return;
        if (workspace.data) {
            patchWorkspace({
                ...workspace.data,
                id: props.workspace,
                defaultTaxCode: v,
                defaultTaxRate: v ? getTaxRateFromCode(v) : 0,
                documentIsCurrent: false
            });
            setShowMenu(false);
        }
    }

    const getTaxRateFromCode = (taxCode: string) => {
        return taxCodes.data?.filter(f => f.name == taxCode)?.[0]?.rate
    }
    
    const getTaxCodeDefaultHeader = () => {
        if(val) {
            return `(${val})`
        }
        return ''
    }

    const taxCodeOptions = [...(taxCodes?.data?.map(taxCode => (
        {
            key: taxCode.name ?? '',
            text: `${taxCode.name ?? ''} (${taxCode.rate?.toString()}%)`
        }
    )) ?? [])];

    const menuItems: IContextualMenuItem[] = [
        {key: 'defaultHeader', itemType: ContextualMenuItemType.Header, text: 'Default for lines without a tax code'},
        {
            onRender: () =>
                <div style={{margin: '1em'}}>
                    <Dropdown
                        placeholder={'Not Set'}
                        selectedKey={workspace.data?.defaultTaxCode}
                        options={[...(taxCodes?.data?.map(taxCode => (
                            {
                                key: taxCode.name ?? '',
                                text: `${taxCode.name ?? ''} (${taxCode.rate?.toString()}%)`
                            }
                        )) ?? [])
                        ]}
                        onChange={(e,d) => {
                            if (d) {
                                update(d.key.toString())
                            }
                        }}
                    />
                </div>,
            key: 'taxCode',
        },
        {key: 'divider', itemType: ContextualMenuItemType.Divider},
        {key: 'allHeader', itemType: ContextualMenuItemType.Header, text: `Set every line (${items.length}) to`},
        {
            key: 'applyAll',
            onRender: () =>
                <div style={{margin: '0 1em 1em'}}>
                    <Dropdown
                        placeholder={applying ? 'Applying…' : 'Choose a tax code for all lines'}
                        disabled={applying || items.length === 0}
                        selectedKey={null}
                        options={taxCodeOptions}
                        onChange={(e, d) => { if (d) applyToAll(d.key.toString()); }}
                    />
                </div>
        }
    ]
    return (<>
        <div
            onClick={() => {
                setShowMenu(!showMenu);
            }}
            style={{
                cursor: 'pointer',
                width: '400px',
            }}
        >
            <span ref={headerRef}>
                Tax Code {getTaxCodeDefaultHeader()}
                <ContextualMenu items={menuItems} hidden={!showMenu} target={headerRef} onDismiss={() => setShowMenu(false)}/>
                <Icon iconName={'Edit'} style={{marginLeft: '0.5em'}}/>
            </span>
        </div>
    </>);
}

export default WorkspaceTaxCodeHeader;